import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { HOST_API } from 'src/config-global';
import { endpoints } from 'src/utils/api-endpoints';
import { accessTokenRef } from 'src/auth/context/jwt/access-token-ref';

export const PATCH_VERSION = 'patch-version';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: true,
});

type RefreshResponse = { accessToken: string };

type FailedRequest = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((req) => {
    if (error) req.reject(error);
    else req.resolve(token as string);
  });
  failedQueue = [];
};

// REQUEST INTERCEPTOR
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = accessTokenRef.current;

    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Super-admin impersonation: if the user has switched into an org via the
    // Switch To flow, the org id is stored in localStorage. We forward it via
    // an "X-Acting-Org-Id" header so the backend can scope the request to that
    // org. The backend ignores the header for non-super-admin users.
    try {
      const actingOrgId = typeof window !== 'undefined' ? localStorage.getItem('currentOrg') : null;
      if (actingOrgId) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)['X-Acting-Org-Id'] = actingOrgId;
      }
    } catch {
      /* no-op: localStorage may be unavailable */
    }

    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const version = response.headers[PATCH_VERSION];
    if (version) {
      response.data = { ...response.data, version };
    }

    // ✅ If backend silently rotated the access token via authMiddleware,
    // pick it up from the response Authorization header
    const newToken = response.headers.authorization?.replace('Bearer ', '');
    if (newToken) {
      accessTokenRef.current = newToken;
    }

    return response;
  },

  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error?.response?.data ?? error?.message ?? 'Something went wrong');
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post<RefreshResponse>(
        `${HOST_API}${endpoints.auth.refreshToken}`,
        {},
        { withCredentials: true }
      );

      const newToken = res.data.accessToken;
      if (!newToken) throw new Error('No token in refresh response');

      accessTokenRef.current = newToken;

      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return await axiosInstance(originalRequest);

    } catch (err) {
      processQueue(err, null);
      accessTokenRef.current = null;
      localStorage.removeItem('userData');
      setTimeout(() => {
        window.location.replace('/auth/login');
      }, 0);
      return await Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

// FETCHER
export const fetcher = async (
  args: string | [string, InternalAxiosRequestConfig]
) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

export default axiosInstance;